import { createSlice, ListenerMiddlewareInstance, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../../configuration/setup/store';
import { ServiceOrder, ServiceOrderStatus } from '../serviceOrders/serviceOrders/serviceOrder.types';
import { ServiceOrdersTableColumns } from '../serviceOrders/serviceOrders/table/ServiceOrdersTableColumns';
import { SortDirection, TableSort } from '../sharedComponents/table/SortedTableHeader';
import { ServiceOrderFilters, ServiceOrderModalFilters } from '../serviceOrders/shared/filter.types';
import { ServiceCategory } from '../serviceOrders/shared/commonServiceOrder.types';
import { ServiceOption, ServiceOptionStatus } from '../serviceOrders/serviceOptions/serviceOption.types';
import { ServiceOptionsTableColumns } from '../serviceOrders/serviceOptions/table/ServiceOptionsTableColumns';
import { matchServiceOrderReceived } from '../api/serviceOrdersApi';
import { matchServiceOptionReceived } from '../api/serviceOptionsApi';
import { createDebouncedValue } from '../../../configuration/setup/debouncedStateUpdate';
import { DEBOUNCE_TIME } from '../debounceConfig';

export const NO_SELECTED_SERVICE = undefined;

export type ServiceSortingState = {
    [ServiceCategory.CURRENT]: TableSort<ServiceOrdersTableColumns>;
    [ServiceCategory.OPTIONS]: TableSort<ServiceOptionsTableColumns>;
    [ServiceCategory.ARCHIVE]: TableSort<ServiceOrdersTableColumns>;
};

export type ServiceOrdersState = {
    selectedServiceOrder: ServiceOrder | undefined;
    selectedServiceOption: ServiceOption | undefined;
    filters: ServiceOrderFilters;
    search: string;
    debouncedSearch: string;
    sorting: ServiceSortingState;
    category: ServiceCategory;
};

export const initialSortingState: ServiceSortingState = {
    [ServiceCategory.CURRENT]: {
        dir: SortDirection.DESCENDING,
        column: ServiceOrdersTableColumns.EXECUTION_DATE_END,
    },
    [ServiceCategory.OPTIONS]: {
        dir: SortDirection.DESCENDING,
        column: ServiceOptionsTableColumns.ORDER_ISSUE_DATE,
    },
    [ServiceCategory.ARCHIVE]: {
        dir: SortDirection.DESCENDING,
        column: ServiceOrdersTableColumns.EXECUTION_DATE_END,
    },
};

export const initialServiceOrdersState: ServiceOrdersState = {
    selectedServiceOrder: NO_SELECTED_SERVICE,
    selectedServiceOption: NO_SELECTED_SERVICE,
    filters: {
        statusFilterForOrders: [],
        statusFilterForOptions: [],
        modalFilters: {
            startDateFilter: undefined,
            endDateFilter: undefined,
            serviceTypeFilter: [],
            locationFilter: undefined,
        },
    },
    search: '',
    debouncedSearch: '',
    sorting: initialSortingState,
    category: ServiceCategory.CURRENT,
};

const debouncedSearchSetup = createDebouncedValue<string>({
    typePrefix: 'serviceOrders/search',
    debounceTime: DEBOUNCE_TIME,
});

export const serviceOrdersSlice = createSlice({
    name: 'serviceOrders',
    initialState: initialServiceOrdersState,
    reducers: {
        selectServiceOrder: (state: ServiceOrdersState, action: PayloadAction<ServiceOrder>) => {
            state.selectedServiceOrder = action.payload;
        },
        selectServiceOption: (state: ServiceOrdersState, action: PayloadAction<ServiceOption>) => {
            state.selectedServiceOption = action.payload;
        },
        deselectServiceOrder: (state: ServiceOrdersState) => {
            state.selectedServiceOrder = NO_SELECTED_SERVICE;
        },
        deselectServiceOption: (state: ServiceOrdersState) => {
            state.selectedServiceOption = NO_SELECTED_SERVICE;
        },
        setCategory: (state: ServiceOrdersState, action: PayloadAction<ServiceCategory>) => {
            if (action.payload !== state.category) {
                state.filters.statusFilterForOrders = initialServiceOrdersState.filters.statusFilterForOrders;
                state.filters.statusFilterForOptions = initialServiceOrdersState.filters.statusFilterForOptions;
                state.selectedServiceOrder = NO_SELECTED_SERVICE;
                state.selectedServiceOption = NO_SELECTED_SERVICE;
            }
            state.category = action.payload;
        },
        jumpToServiceOption: (state: ServiceOrdersState, action: PayloadAction<string>) => {
            state.category = ServiceCategory.OPTIONS;
            state.selectedServiceOrder = NO_SELECTED_SERVICE;
            state.selectedServiceOption = NO_SELECTED_SERVICE;
            state.filters = initialServiceOrdersState.filters;
            state.search = action.payload;
            state.debouncedSearch = action.payload;
        },
        jumpToServiceOrder: (state: ServiceOrdersState, action: PayloadAction<string>) => {
            state.category = ServiceCategory.CURRENT;
            state.selectedServiceOrder = NO_SELECTED_SERVICE;
            state.selectedServiceOption = NO_SELECTED_SERVICE;
            state.filters = initialServiceOrdersState.filters;
            state.search = action.payload;
            state.debouncedSearch = action.payload;
        },
        setServiceOrderStatusFilter: (state: ServiceOrdersState, action: PayloadAction<ServiceOrderStatus[]>) => {
            state.filters.statusFilterForOrders = action.payload;
        },
        setServiceOptionStatusFilter: (state: ServiceOrdersState, action: PayloadAction<ServiceOptionStatus[]>) => {
            state.filters.statusFilterForOptions = action.payload;
        },
        setModalFilters: (state: ServiceOrdersState, action: PayloadAction<ServiceOrderModalFilters>) => {
            state.filters.modalFilters = action.payload;
        },
        setSortingForServiceOrders: (
            state: ServiceOrdersState,
            action: PayloadAction<{
                sort: TableSort<ServiceOrdersTableColumns>;
            }>,
        ) => {
            if (state.category === ServiceCategory.CURRENT || state.category === ServiceCategory.ARCHIVE) {
                state.sorting[state.category] = action.payload.sort;
            }
        },
        setSortingForServiceOptions: (
            state: ServiceOrdersState,
            action: PayloadAction<{
                sort: TableSort<ServiceOptionsTableColumns>;
            }>,
        ) => {
            state.sorting[ServiceCategory.OPTIONS] = action.payload.sort;
        },
        setInitialState: () => initialServiceOrdersState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(debouncedSearchSetup.setValueAction, (state, action) => {
                state.search = action.payload.value;
            })

            .addCase(debouncedSearchSetup.setDebouncedValueAction, (state, action) => {
                state.debouncedSearch = action.payload;
            })
            .addMatcher(matchServiceOrderReceived, (state, action) => {
                const selected = state.selectedServiceOrder;
                if (selected !== undefined) {
                    if (!action.payload.items?.find((it) => it.id === selected.id)) {
                        state.selectedServiceOrder = NO_SELECTED_SERVICE;
                    }
                }
            })
            .addMatcher(matchServiceOptionReceived, (state, action) => {
                const selected = state.selectedServiceOption;
                if (selected !== undefined) {
                    if (!action.payload.items?.find((it) => it.id === selected.id)) {
                        state.selectedServiceOption = NO_SELECTED_SERVICE;
                    }
                }
            });
    },
});

export const getSelectedServiceOrder = (state: RootState) => state.portal.serviceOrders.selectedServiceOrder;
export const getSelectedServiceOption = (state: RootState) => state.portal.serviceOrders.selectedServiceOption;
export const getServiceOrderStatusFilter = (state: RootState) =>
    state.portal.serviceOrders.filters.statusFilterForOrders;
export const getServiceOptionStatusFilter = (state: RootState) =>
    state.portal.serviceOrders.filters.statusFilterForOptions;
export const getServiceOrderModalFilters = (state: RootState): ServiceOrderModalFilters =>
    state.portal.serviceOrders.filters.modalFilters;
export const getServiceOrderSearch = (state: RootState) => state.portal.serviceOrders.search;
export const getDebouncedServiceOrderSearch = (state: RootState) => state.portal.serviceOrders.debouncedSearch;
export const getServiceOrderSort = (state: RootState): TableSort<ServiceOrdersTableColumns> =>
    ServiceCategory.ARCHIVE === state.portal.serviceOrders.category
        ? state.portal.serviceOrders.sorting[ServiceCategory.ARCHIVE]
        : state.portal.serviceOrders.sorting[ServiceCategory.CURRENT];
export const getServiceOptionSort = (state: RootState): TableSort<ServiceOptionsTableColumns> =>
    state.portal.serviceOrders.sorting[ServiceCategory.OPTIONS];
export const getServiceOrderCategory = (state: RootState) => state.portal.serviceOrders.category;

export const setServiceOrderSearch = debouncedSearchSetup.setValueAction;

export const addSliceListeners = (listenerMiddleware: ListenerMiddlewareInstance) => {
    listenerMiddleware.startListening(debouncedSearchSetup.listener);
};
